import { useMemo, useState } from "react";
import type { FilterItem } from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import {
  drybulkCargoOperationsService,
  type CargoOperationSearchRequest,
  type OperationType,
} from "@services/drybulkCargoOperationsApi";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import {
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  GRID_REFETCH_INTERVAL_MS,
} from "../constants";
import { wholeNumberFilter } from "../utils/wholeNumberFilter";
import { useVesselCallOperationContext } from "./useVesselCallOperationContext";

export const GRID_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const SEARCH_INQUIRY_ID = "drybulk_cargo_operations.grid.search";

const inquiry = (action: string) => ({
  module: DRYBULK_CARGO_OPS_TELEMETRY.module,
  screen: DRYBULK_CARGO_OPS_TELEMETRY.gridScreen,
  action,
  entityType: "cargo_operation",
});

const filterValue = (filters: FilterItem[], field: string) =>
  filters.find((item) => item.field === field)?.value;

const rangeBounds = (filters: FilterItem[], field: string) => {
  const value = filterValue(filters, field);
  return value !== null && typeof value === "object" ? value : {};
};

const numberBound = (bound: string | undefined): number | undefined => {
  if (bound === undefined || bound.trim() === "") {
    return undefined;
  }
  const parsed = Number(bound);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const textBound = (bound: string | undefined): string | undefined =>
  bound !== undefined && bound.trim() !== "" ? bound : undefined;

export const buildGridCriteria = (
  filters: FilterItem[],
  vesselCallId: string | undefined,
  page: number,
  pageSize: number,
): CargoOperationSearchRequest => {
  const status = filterValue(filters, "status");
  const operationType = filterValue(filters, "operationType");
  const gradeSize = filterValue(filters, "gradeSize");
  const operationNumber = wholeNumberFilter(filterValue(filters, "operationNumber"));
  const nominationRefOrderNumber = wholeNumberFilter(
    filterValue(filters, "nominationRefOrderNumber"),
  );
  const quantity = rangeBounds(filters, "quantity");
  const totalQuantityFrom = numberBound(quantity.from);
  const totalQuantityTo = numberBound(quantity.to);
  const ats = rangeBounds(filters, "ats");
  const atsFrom = textBound(ats.from);
  const atsTo = textBound(ats.to);
  const atc = rangeBounds(filters, "atc");
  const atcFrom = textBound(atc.from);
  const atcTo = textBound(atc.to);
  return {
    ...(vesselCallId ? { vesselCallId } : {}),
    ...(typeof status === "string" && status !== "" ? { statuses: [status] } : {}),
    ...(operationNumber !== undefined ? { operationNumber } : {}),
    ...(nominationRefOrderNumber !== undefined ? { nominationRefOrderNumber } : {}),
    ...(typeof operationType === "string" && operationType !== ""
      ? { operationTypes: [operationType as OperationType] }
      : {}),
    ...(typeof gradeSize === "string" && gradeSize !== "" ? { gradeSizes: [gradeSize] } : {}),
    ...(totalQuantityFrom !== undefined ? { totalQuantityFrom } : {}),
    ...(totalQuantityTo !== undefined ? { totalQuantityTo } : {}),
    ...(atsFrom ? { atsFrom } : {}),
    ...(atsTo ? { atsTo } : {}),
    ...(atcFrom ? { atcFrom } : {}),
    ...(atcTo ? { atcTo } : {}),
    sortBy: "CREATED_AT",
    sortDirection: "DESC",
    pageNumber: page - 1,
    pageSize,
  };
};

export const useCargoOperationsSearch = ({
  pausePolling = false,
}: { pausePolling?: boolean } = {}) => {
  const { vesselCallId } = useVesselCallOperationContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filters, setFilters] = useState<FilterItem[]>([]);

  const criteria = useMemo(
    () => buildGridCriteria(filters, vesselCallId, page, pageSize),
    [filters, vesselCallId, page, pageSize],
  );

  const query = useQuery({
    queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.search(criteria),
    queryFn: () => drybulkCargoOperationsService.search(criteria),
    refetchInterval: pausePolling ? false : GRID_REFETCH_INTERVAL_MS,
    refetchOnWindowFocus: !pausePolling,
    placeholderData: keepPreviousData,
    retry: false,
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
    error: query.error,
    dataUpdatedAt: query.dataUpdatedAt,
  });

  const applyFilters = (next: FilterItem[]) => {
    startInquiryTiming(SEARCH_INQUIRY_ID, inquiry("filter_cargo_operations"));
    setFilters(next);
    setPage(1);
  };

  const clearFilters = () => {
    if (filters.length > 0) {
      startInquiryTiming(SEARCH_INQUIRY_ID, inquiry("filter_cargo_operations"));
    }
    setFilters([]);
    setPage(1);
  };

  const changePage = (nextPage: number, nextPageSize: number) => {
    if (nextPage !== page || nextPageSize !== pageSize) {
      startInquiryTiming(SEARCH_INQUIRY_ID, inquiry("page_cargo_operations"));
    }
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  return {
    rows: query.data?.records ?? [],
    totalRecordCount: query.data?.totalRecordCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    page,
    pageSize,
    filters,
    applyFilters,
    clearFilters,
    changePage,
    criteria,
  };
};
